import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, MessageSquare, Settings, FolderKanban, Cpu, Code, Library, Image, Brain, Bot, GraduationCap, Microscope, Beaker, Download } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useIsMobile } from '@/hooks/use-mobile';

interface MobileNavigationProps {
  navItems: any[]; // Adjust type as needed
  collapsed: boolean;
  onToggleCollapse: () => void;
  UserActionsComponent: React.ComponentType;
  DownloadButtonComponent: React.ComponentType;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  navItems,
  UserActionsComponent,
  DownloadButtonComponent,
}) => {
  const location = useLocation();
  const isMobile = useIsMobile();

  const [openCollapsibles, setOpenCollapsibles] = React.useState<Record<string, boolean>>({});

  const handleCollapsibleToggle = React.useCallback((path: string) => {
    setOpenCollapsibles(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  }, []);

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-2 flex justify-around items-center z-50 md:hidden">
      <Link to="/" className="flex flex-col items-center text-xs text-muted-foreground hover:text-foreground">
        <MessageSquare size={20} />
        <span>Chat</span>
      </Link>
      <Link to="/projects" className="flex flex-col items-center text-xs text-muted-foreground hover:text-foreground">
        <FolderKanban size={20} />
        <span>Projects</span>
      </Link>
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="flex flex-col items-center text-xs text-muted-foreground hover:text-foreground h-auto p-0">
            <Menu size={20} />
            <span>Menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-4">
          <h2 className="text-xl font-bold mb-4">Navigation</h2>
          <div className="space-y-2">
            {navItems.map((item) => (
              <div key={item.path} className="mb-1">
                {item.hasChildren ? (
                  <Collapsible open={openCollapsibles[item.path]} onOpenChange={() => handleCollapsibleToggle(item.path)} className="w-full">
                    <CollapsibleTrigger className="w-full">
                      <div
                        className={`flex items-center gap-2 rounded-md px-3 py-2 w-full transition-colors cursor-pointer ${
                          item.active
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent'
                        }`}
                      >
                        {item.icon}
                        <span className="flex-1 text-left">{item.label}</span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-8 pt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                            child.active
                              ? 'bg-accent text-accent-foreground'
                              : 'hover:bg-accent'
                          }`}
                        >
                          {child.icon}
                          <span>{child.label}</span>
                        </Link>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ) : item.onClick ? (
                  <button
                    onClick={item.onClick}
                    className={`flex items-center gap-2 w-full rounded-md px-3 py-2 transition-colors ${
                      item.active
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 transition-colors ${
                      item.active
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    {item.isNew && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-primary text-primary-foreground" style={{ letterSpacing: '0.03em' }}>
                        NEW
                      </span>

                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-2">
            {/* <DownloadButtonComponent /> */}
            {/* <UserActionsComponent /> */}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileNavigation;